import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner, faPlay, faStop } from '@fortawesome/free-solid-svg-icons';
import ModalOverlay from '../../../components/ui/ModalOverlay/ModalOverlay';
import s from '../CaseFilePage.module.css';

interface RiskForm {
  clientDetailsVerified: string;
  vulnerableClient: string;
  idPresent: string;
  poaPresent: string;
  amlPresent: string;
  riskLevel: string;
  caseOutcome: string;
  rejectReason: string;
  extraComments: string;
}

interface RiskAssessmentModalProps {
  riskForm: RiskForm;
  setRiskForm: React.Dispatch<React.SetStateAction<RiskForm>>;
  riskTimerRunning: boolean;
  riskTimerElapsed: number;
  riskTimerStart: number | null;
  setRiskTimerStart: (v: number | null) => void;
  setRiskTimerRunning: (v: boolean) => void;
  riskTimerRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>;
  handleSaveRiskAssessment: () => void;
  isPending: boolean;
  formatElapsed: (ms: number) => string;
  onClose: () => void;
}

export default function RiskAssessmentModal({
  riskForm,
  setRiskForm,
  riskTimerRunning,
  riskTimerElapsed,
  riskTimerStart: _riskTimerStart,
  setRiskTimerStart,
  setRiskTimerRunning,
  riskTimerRef,
  handleSaveRiskAssessment,
  isPending,
  formatElapsed,
  onClose,
}: RiskAssessmentModalProps) {
  const handleClose = () => {
    if (riskTimerRunning && !window.confirm('Timer is running. Close without saving?')) return;
    onClose();
    setRiskTimerRunning(false);
    if (riskTimerRef.current) clearInterval(riskTimerRef.current);
  };

  return (
    <ModalOverlay onClose={handleClose} title="Risk Assessment">
      <div className={s.modalForm}>
        {/* Timer */}
        <div className={s.riskTimerRow}>
          <span className={s.riskTimerDisplay}>{formatElapsed(riskTimerElapsed)}</span>
          {!riskTimerRunning ? (
            <button
              className={s.btnPrimary}
              onClick={() => {
                setRiskTimerStart(Date.now() - riskTimerElapsed);
                setRiskTimerRunning(true);
              }}
            >
              <FontAwesomeIcon icon={faPlay} /> Start
            </button>
          ) : (
            <button
              className={s.btnDanger}
              onClick={() => {
                setRiskTimerRunning(false);
                if (riskTimerRef.current) clearInterval(riskTimerRef.current);
              }}
            >
              <FontAwesomeIcon icon={faStop} /> Stop
            </button>
          )}
        </div>

        {/* Form fields */}
        {(
          [
            { key: 'clientDetailsVerified', label: 'Client Details Verified' },
            { key: 'vulnerableClient', label: 'Vulnerable Client' },
            { key: 'idPresent', label: 'ID Present' },
            { key: 'poaPresent', label: 'POA Present' },
            { key: 'amlPresent', label: 'AML Present' },
          ] as const
        ).map(({ key, label }) => (
          <div key={key} className={s.formGroup}>
            <label>{label}</label>
            <select
              value={riskForm[key]}
              onChange={(e) => setRiskForm((p) => ({ ...p, [key]: e.target.value }))}
            >
              <option value="">Select…</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        ))}

        <div className={s.formGroup}>
          <label>Risk Level</label>
          <select
            value={riskForm.riskLevel}
            onChange={(e) => setRiskForm((p) => ({ ...p, riskLevel: e.target.value }))}
          >
            <option value="">Select…</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
        </div>

        <div className={s.formGroup}>
          <label>Case Outcome</label>
          <select
            value={riskForm.caseOutcome}
            onChange={(e) => setRiskForm((p) => ({ ...p, caseOutcome: e.target.value }))}
          >
            <option value="">Select…</option>
            <option value="Accept">Accept</option>
            <option value="Reject">Reject</option>
            <option value="Refer">Refer</option>
          </select>
        </div>

        {riskForm.caseOutcome === 'Reject' && (
          <div className={s.formGroup}>
            <label>Reject Reason</label>
            <textarea
              rows={2}
              value={riskForm.rejectReason}
              onChange={(e) => setRiskForm((p) => ({ ...p, rejectReason: e.target.value }))}
            />
          </div>
        )}

        <div className={s.formGroup}>
          <label>Extra Comments</label>
          <textarea
            rows={2}
            value={riskForm.extraComments}
            onChange={(e) => setRiskForm((p) => ({ ...p, extraComments: e.target.value }))}
          />
        </div>

        <div className={s.modalActions}>
          <button className={s.btnSecondary} onClick={handleClose}>
            Cancel
          </button>
          <button className={s.btnPrimary} onClick={handleSaveRiskAssessment} disabled={isPending}>
            {isPending ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faCheck} />
            )}
            Save Assessment
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
